import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  alpha,
} from '@mui/material';
import {
  Email,
  Business,
  Group,
  AccessTime,
  Person,
  TrendingUp,
  CalendarToday,
  WorkOutline,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { apiService } from '../../services/apiService';
import { colors } from '../../theme/theme';
import { CorporateLead } from './types';
import { StatCard, SearchBar, DATA_GRID_SX, tableCardSx, formatSubmittedDate, STAT_CARDS_GRID_SX, DATA_GRID_CONTAINER_SX, isThisMonth, fetchAllPages } from './shared';

export function CorporateTab() {
  const [leads, setLeads] = useState<CorporateLead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const allLeads = await fetchAllPages<CorporateLead>(async (page, pageSize) => {
        const r = await apiService.getCorporateLeads({ page, pageSize });
        return { total: r.total || 0, items: r.leads || [] };
      });
      allLeads.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setLeads(allLeads);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch corporate leads');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredLeads = useMemo(() => {
    const s = searchTerm.toLowerCase().trim();
    return leads.filter(
      (lead) =>
        s === '' ||
        lead.name?.toLowerCase().includes(s) ||
        lead.companyName?.toLowerCase().includes(s) ||
        lead.workEmail?.toLowerCase().includes(s) ||
        lead.interest?.toLowerCase().includes(s) ||
        lead.teamSize?.toLowerCase().includes(s)
    );
  }, [leads, searchTerm]);

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: alpha(colors.primary, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Person sx={{ fontSize: 20, color: colors.primary }} />
          </Box>
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: colors.textPrimary,
            }}
          >
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'companyName',
      headerName: 'Company',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          <Business sx={{ fontSize: 18, color: colors.warning }} />
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: colors.textPrimary,
            }}
            fontWeight="500"
          >
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'workEmail',
      headerName: 'Work Email',
      width: 260,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          <Email sx={{ fontSize: 18, color: colors.info }} />
          <Typography
            variant="body2"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: colors.textPrimary,
            }}
          >
            {params.value || 'N/A'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'teamSize',
      headerName: 'Team Size',
      width: 140,
      renderCell: (params) => (
        <Box sx={{ py: 1 }}>
          {params.value ? (
            <Chip
              label={params.value}
              size="small"
              icon={<Group sx={{ fontSize: 14 }} />}
              sx={{
                backgroundColor: alpha(colors.success, 0.1),
                color: colors.success,
                border: `1px solid ${alpha(colors.success, 0.3)}`,
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 28,
                '& .MuiChip-icon': { color: colors.success },
              }}
            />
          ) : (
            <Typography variant="body2" color={colors.textSecondary} fontStyle="italic">
              N/A
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'interest',
      headerName: 'Interested In',
      width: 220,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          {params.value ? (
            <>
              <WorkOutline sx={{ fontSize: 18, color: colors.primary }} />
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: colors.textPrimary,
                }}
              >
                {params.value}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color={colors.textSecondary} fontStyle="italic">
              Not specified
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: params.value ? colors.textPrimary : colors.textSecondary,
            fontStyle: params.value ? 'normal' : 'italic',
            py: 1,
          }}
        >
          {params.value || 'No message'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Submitted On',
      width: 180,
      renderCell: (params) => {
        const { date, time } = formatSubmittedDate(params.value);
        return (
          <Box display="flex" alignItems="center" gap={1} sx={{ py: 1 }}>
            <AccessTime sx={{ fontSize: 18, color: colors.textSecondary }} />
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {date}
              </Typography>
              <Typography variant="caption" color={colors.textSecondary} display="block">
                {time}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const thisMonth = leads.filter((l) => isThisMonth(l.createdAt)).length;

  const uniqueCompanies = new Set(leads.map((l) => l.companyName?.toLowerCase())).size;

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={STAT_CARDS_GRID_SX}>
        <StatCard
          label="Total Leads"
          value={leads.length}
          icon={<TrendingUp sx={{ fontSize: 28, color: colors.primary }} />}
          color={colors.primary}
        />
        <StatCard
          label="This Month"
          value={thisMonth}
          icon={<CalendarToday sx={{ fontSize: 28, color: colors.info }} />}
          color={colors.info}
        />
        <StatCard
          label="Companies"
          value={uniqueCompanies}
          icon={<Business sx={{ fontSize: 28, color: colors.warning }} />}
          color={colors.warning}
        />
      </Box>

      <SearchBar
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search by name, company, email, or interest..."
      />

      <Card sx={tableCardSx}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={DATA_GRID_CONTAINER_SX}>
            <DataGrid
              rows={filteredLeads}
              columns={columns}
              getRowId={(row) => row._id}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              disableRowSelectionOnClick
              getRowHeight={() => 75}
              sx={DATA_GRID_SX}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
